import type {
  SectionFormFragment,
  BoxFragment,
  FormFragment,
} from '@seek/cmsu-cms-connect';
import type { Country, SiteName } from '@seek/melways-sites';
import { Box, ContentBlock } from 'braid-design-system';

import { BoxWithDecorator } from '../../shared/decorations';
import {
  getBlockBorderProps,
  getBoxBackgroundProps,
  getBoxShadowProps,
} from '../../shared/helper';
import { BoxDecoratorGroup } from '../BoxDecoratorGroup/BoxDecoratorGroup';
import { CForm } from '../CForm/CForm';

export const SectionForm = ({
  sectionForm,
  melwaysLocale,
  sourceName,
  country,
  privacyUrl,
  recaptchaKey,
  site,
  wptSalesForceApi,
  sectionName,
}: {
  sectionForm: SectionFormFragment;
  melwaysLocale: string;
  sourceName: string;
  country: Country;
  privacyUrl: string;
  recaptchaKey: string;
  site: SiteName;
  wptSalesForceApi: string;
  sectionName?: string;
}) => {
  const form = sectionForm?.form?.form as FormFragment;

  if (form === undefined) {
    return null;
  }

  const sectionFormBox = sectionForm?.formBox as BoxFragment;
  const paddingProps = sectionFormBox?.background
    ? {
        paddingX: {
          mobile: 'medium',
          tablet: sectionFormBox?.paddingWidth || 'xlarge',
        },
        paddingY: sectionFormBox?.paddingHeight || 'xlarge',
      }
    : {
        paddingX: {
          mobile: 'small',
          tablet: 'small',
          desktop: sectionFormBox?.paddingWidth || 'none',
        },
        paddingY: sectionFormBox?.paddingHeight || undefined,
      };

  return (
    <ContentBlock width={sectionFormBox?.width || 'medium'}>
      <Box
        {...getBlockBorderProps(sectionFormBox?.blockBorder)}
        borderRadius={{
          mobile: 'none',
          tablet: sectionFormBox?.borderRadius || undefined,
        }}
        paddingX={sectionFormBox?.blockBorder ? 'gutter' : 'none'}
        paddingY={sectionFormBox?.blockBorder ? 'medium' : 'none'}
        position="relative"
      >
        <BoxDecoratorGroup decorators={sectionFormBox?.decorationAsset} />
        <Box
          paddingY={sectionFormBox?.paddingHeight || 'small'}
          paddingX={
            sectionFormBox?.blockBorder
              ? { mobile: 'small', desktop: 'none' }
              : 'none'
          }
        >
          <BoxWithDecorator
            customDecoration={sectionFormBox?.customDecoration}
            textAlign={
              sectionFormBox?.textAlign || sectionFormBox?.textAlign || 'left'
            }
            // Keep the padding to 'none' here when there is no background.
            // Changing it will break alignment of tiles. To add padding to the blocks, adjust the padding of the parent box ^.
            {...(paddingProps as React.ComponentProps<typeof Box>)}
            className="sk-form-block"
            {...getBoxBackgroundProps(sectionFormBox?.background)}
            {...getBoxShadowProps(sectionFormBox?.blockBorder)}
            borderRadius={sectionFormBox?.borderRadius || 'none'}
          >
            <CForm
              sectionName={sectionName}
              locale={melwaysLocale}
              sourceName={sourceName}
              privacyUrl={privacyUrl}
              recaptchaKey={recaptchaKey}
              wptSalesForceApi={wptSalesForceApi}
              country={country}
              site={site}
              form={sectionForm?.form?.form as FormFragment}
            />
          </BoxWithDecorator>
        </Box>
      </Box>
    </ContentBlock>
  );
};
